import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { FlagBadges } from "@/components/qa/FlagBadge";
import { useAuth } from "@/hooks/useAuth";
import { useDebounced } from "@/hooks/useDebounced";
import { useEvents } from "@/hooks/useEvents";
import { useKeyboard } from "@/hooks/useKeyboard";
import { useReference } from "@/hooks/useReference";
import { isolates as isolatesApi, teams as teamsApi, type IsolateQuery } from "@/lib/api";
import { cacheIsolates, cachedIsolates } from "@/lib/db";
import { formatDate, formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { Isolate, Team } from "@/lib/types";
import { cn } from "@/lib/utils";
import { EMPTY_FILTERS, FilterBar, type Filters } from "./FilterBar";

/**
 * The master view (docs/08 §8.6) - the Data Manager's home.
 *
 * Live: a new isolate matching the current filters appears at the top with a
 * brief highlight (M-02). Falls back to the cached rows when offline.
 */
export function MasterView() {
  const { isElevated, canExport } = useAuth();
  const { organismName } = useReference();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [rows, setRows] = useState<Isolate[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);

  const searchRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounced(filters.q, 300);

  const buildQuery = useCallback(
    (after?: string): IsolateQuery => ({
      q: debouncedQuery || undefined,
      team: filters.team || undefined,
      status: filters.status || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      specimen_type: filters.specimen_type || undefined,
      organism_code: filters.organism_code || undefined,
      has_open_flag:
        filters.has_open_flag === "" ? undefined : filters.has_open_flag === "true",
      after,
      limit: 50,
      include_total: true,
    }),
    [debouncedQuery, filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await isolatesApi.list(buildQuery());
      setRows(page.items);
      setCursor(page.next_cursor);
      setTotal(page.total);
      setOffline(false);
      // Keep them for offline viewing (docs/08 §8.6).
      void cacheIsolates(page.items);
    } catch {
      // docs/08 §8.6: offline shows the last rows the user saw.
      setRows(await cachedIsolates());
      setCursor(null);
      setOffline(true);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void teamsApi.list().then(setTeams).catch(() => undefined);
  }, []);

  // M-02: new rows appear within two seconds of being saved anywhere.
  useEvents(
    useCallback(
      (event) => {
        if (event.type !== "isolate.created" && event.type !== "isolate.updated") return;

        void (async () => {
          try {
            const isolate = await isolatesApi.get(event.data.id);
            setRows((current) => {
              const without = current.filter((row) => row.id !== isolate.id);
              return [isolate, ...without];
            });
            setHighlighted((current) => new Set(current).add(isolate.id));
            setTimeout(
              () =>
                setHighlighted((current) => {
                  const next = new Set(current);
                  next.delete(isolate.id);
                  return next;
                }),
              4000,
            );
          } catch {
            // The row may be outside our filters or scope; ignore it.
          }
        })();
      },
      [],
    ),
  );

  const loadMore = async () => {
    if (!cursor) return;
    const page = await isolatesApi.list(buildQuery(cursor));
    setRows((current) => [...current, ...page.items]);
    setCursor(page.next_cursor);
    void cacheIsolates(page.items);
  };

  // docs/08 §8.6: J and K move, Enter opens, / focuses search, E exports.
  useKeyboard([
    {
      key: "j",
      description: "Next row",
      handler: () => setActiveIndex((i) => Math.min(i + 1, rows.length - 1)),
    },
    { key: "k", description: "Previous row", handler: () => setActiveIndex((i) => Math.max(i - 1, 0)) },
    {
      key: "Enter",
      description: "Open the selected row",
      handler: () => {
        const row = rows[activeIndex];
        if (row) navigate(`/isolates/${row.id}`);
      },
    },
    { key: "/", description: "Focus search", handler: () => searchRef.current?.focus() },
    {
      key: "e",
      description: "Export",
      handler: () => {
        if (canExport) navigate("/export");
      },
    },
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">
          {isElevated ? strings.master.title : "My isolates"}
        </h1>
        <div className="flex items-center gap-2">
          {!offline && <Badge tone="ok">{strings.master.liveUpdates}</Badge>}
          {canExport && (
            <Button size="sm" onClick={() => navigate("/export")}>
              {strings.master.exportCsv}
            </Button>
          )}
        </div>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        teams={teams}
        showTeamFilter={isElevated}
        searchRef={searchRef}
      />

      {offline && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          You are offline. These are the last rows you loaded, and filters are
          not applied to them.
        </p>
      )}

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading && rows.length === 0 ? (
          <div className="p-8">
            <Spinner label="Loading isolates" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title={strings.master.noRows} />
        ) : (
          <>
            <Table caption="Isolates">
              <thead>
                <tr>
                  <Th>Record ID</Th>
                  <Th>Specimen date</Th>
                  <Th>Hospital no.</Th>
                  <Th>Ward</Th>
                  <Th>Specimen</Th>
                  <Th>Organism</Th>
                  <Th>Status</Th>
                  <Th>QA</Th>
                  <Th>Entered by</Th>
                  <Th>Entered at</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    aria-selected={index === activeIndex}
                    onClick={() => navigate(`/isolates/${row.id}`)}
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") navigate(`/isolates/${row.id}`);
                    }}
                    className={cn(
                      "cursor-pointer hover:bg-slate-50",
                      index === activeIndex && "bg-brand-50/60",
                      highlighted.has(row.id) && "animate-pulse bg-brand-50",
                    )}
                  >
                    <Td className="font-mono text-xs">{row.record_id}</Td>
                    <Td>{formatDate(row.specimen_date)}</Td>
                    <Td className="font-mono text-xs">{row.hospital_number ?? "—"}</Td>
                    <Td>{row.ward_raw ?? "—"}</Td>
                    <Td>{row.specimen_type ?? "—"}</Td>
                    <Td className="italic">{organismName(row.organism_code)}</Td>
                    <Td>
                      <StatusBadge status={row.status} />
                    </Td>
                    <Td>
                      <FlagBadges flags={row.qa_flags} />
                    </Td>
                    <Td>{row.author_name ?? "—"}</Td>
                    <Td className="whitespace-nowrap text-xs text-slate-500">
                      {formatDateTime(row.created_at)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
              <p className="text-sm text-slate-500">
                {strings.master.rowsShown(rows.length, total)}
              </p>
              {cursor && (
                <Button variant="secondary" size="sm" onClick={() => void loadMore()}>
                  {strings.common.showMore}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
